// Rolling a recurring task forward to its next period.
//
// This lived as five private functions inside src/pages/logbook/LogbookUpdateTask.jsx,
// which is where the owner picks what a completed task should become. It moved
// here for the reason this folder exists: TaskPogoShareTasksWrite completes a
// task for a visitor holding a share link, a Lambda cannot import a .jsx, and
// two copies of recurrence math would be free to disagree about when the next
// oil change is due. LogbookUpdateTask now imports these.
//
// TWO ANCHORS, and they are not interchangeable:
//
//   "from due"  -- the stored due value plus the interval. Pure arithmetic on
//                  what is already in the row.
//   "from now"  -- today (or the item's live counter reading) plus the interval.
//                  Reads the HOST CLOCK and the item.
//
// The owner's UI offers both and lets them choose. THE SHARE LAMBDA MUST USE
// "from due" ONLY: with no owner present there is nobody to choose, and "from
// now" would make the answer depend on the minute a visitor happened to tap a
// button and on whichever region the Lambda ran in. `rollTaskForward` below is
// that decision, spelled once.
//
// ---------------------------------------------------------------------------
// THE OFFSET RULE. THIS IS THE PART THAT IS NOT PURE ARITHMETIC.
//
// `Common.databaseDateTimeString` is `dayjs(date).format()`, which writes the
// HOST's UTC offset: `...T00:00:00-07:00` from a browser in Pacific,
// `...T07:00:00+00:00` from a Lambda. Those are the same instant and a
// different string -- and `dueDate` is a GSI sort key that
// `tasksByItemIdAndDueDate` compares as a STRING. CLAUDE.md's cross-cutting
// rule already says the two spellings do not sort against each other.
//
// So the roll MUST NOT round-trip through the host clock. `nextDueDate` splits
// the stored string into date / time / offset, does the calendar arithmetic on
// the DATE PART alone, and re-emits with the time and offset it was handed.
// A row written by the browser stays in browser spelling after a Lambda rolls
// it; a row written by a Lambda stays in Lambda spelling. The suite pins this
// by running one fixture under two TZ values and asserting an identical string.
//
// A value that does not match the expected shape is rolled the old way and the
// spelling is whatever the host writes -- the fallback exists so a malformed
// legacy row still recurs rather than silently not recurring, and it is the one
// path where the offset can change.
// ---------------------------------------------------------------------------

import dayjs from 'dayjs';
import {
  DATABASE_DATE_TIME,
  datePlusDays,
  datePlusMonths,
  databaseDateTimeString,
  dateStringToDate,
} from './Common.mjs';
import { counterFromId, todayMidnight, dateMidnight } from './TaskBuckets.mjs';

// The stored spelling, from Common so every reader and writer of it agrees --
// `isDatabaseDateTimeString` is the same regex asked as a question, and the
// share endpoint refuses anything that fails it. Used here for its capture
// groups: date, time, offset.
const STAMP = DATABASE_DATE_TIME;

// Roll a starting date forward by the task's recurrence interval. Months take
// precedence over days (only one is ever set) and use calendar math so the task
// lands on the same day-of-month each period.
export function datePlusInterval(startDate, task) {
  if (task.monthsInterval) {
    return datePlusMonths(startDate, task.monthsInterval);
  }
  if (task.daysInterval) {
    return datePlusDays(startDate, task.daysInterval);
  }
  return null;
}

// True when the task recurs on a calendar.
export function hasDateInterval(task) {
  return Boolean(task && (task.monthsInterval || task.daysInterval));
}

// True when the task recurs on a counter.
export function hasCounterInterval(task) {
  return Boolean(task && task.dueCounter && task.counterInterval);
}

// True when completing the task would actually MOVE something. Deliberately
// asked of the two roll functions rather than of the interval columns: a task
// carrying a daysInterval but no dueDate has an interval and nothing to apply
// it to, and answering "recurring" there would turn a completion into an update
// that writes no change and leaves the row on the list forever.
export function isRecurring(task) {
  return nextDueDate(task) !== null || nextDueCounter(task) !== null;
}

// The next due date, anchored on the STORED due date and written in the stored
// value's own offset. See the header -- this is the offset rule.
export function nextDueDate(task) {
  if (!task || !task.dueDate || !hasDateInterval(task)) {
    return null;
  }

  const parts = STAMP.exec(task.dueDate);
  if (!parts) {
    // Legacy or malformed. Recur anyway, in whatever the host spells.
    return databaseDateTimeString(datePlusInterval(task.dueDate, task));
  }

  const [, datePart, timePart, offsetPart] = parts;
  // The date part alone, as a plain calendar day. Parsing `YYYY-MM-DD` lands on
  // local midnight and formatting it back reads local, so the zone cancels and
  // the result is the same in every region -- which is the whole point.
  const day = dayjs(datePart, 'YYYY-MM-DD');
  const rolled = task.monthsInterval
    ? day.add(task.monthsInterval, 'month')
    : day.add(task.daysInterval, 'day');

  return `${rolled.format('YYYY-MM-DD')}T${timePart}${offsetPart}`;
}

// The next due counter, anchored on the STORED due counter.
export function nextDueCounter(task) {
  if (!hasCounterInterval(task)) {
    return null;
  }
  return task.dueCounter + task.counterInterval;
}

// ---- The "from now" anchors -------------------------------------------------
//
// Offered by the owner's UI only. They read the host clock and the item's live
// meter, so nothing server-side may call them. Each returns null when it would
// only repeat what the "from due" answer already says.

export function dueDateFromNow(task) {
  if (!task || !task.dueDate || !hasDateInterval(task)) {
    return null;
  }
  const today = todayMidnight();
  if (today.getTime() === dateMidnight(dateStringToDate(task.dueDate)).getTime()) {
    return null;
  }
  return databaseDateTimeString(datePlusInterval(today, task));
}

export function dueCounterFromNow(task, item) {
  if (!hasCounterInterval(task)) {
    return null;
  }
  const count = counterFromId(item, task.itemCounterId);
  if (count === -1 || count === task.dueCounter) {
    return null;
  }
  return count + task.counterInterval;
}

// ---- The one server-side decision ------------------------------------------

/**
 * What completing `task` should do, anchored on its stored due values.
 *
 *   { outcome: 'deleted' }                      -- a one-off; the row goes away
 *   { outcome: 'rolled', dueDate, dueCounter }  -- a recurring task's next period
 *
 * `dueDate` / `dueCounter` are present only for the dimension that recurs, so a
 * caller spreads the result into an update input without clearing the other.
 * A task recurring on both moves both, which is what the owner's own UI does.
 */
export function rollTaskForward(task) {
  if (!isRecurring(task)) {
    return { outcome: 'deleted' };
  }

  const result = { outcome: 'rolled' };
  const date = nextDueDate(task);
  if (date !== null) {
    result.dueDate = date;
  }
  const counter = nextDueCounter(task);
  if (counter !== null) {
    result.dueCounter = counter;
  }
  return result;
}
